import React, { useEffect, useState, lazy, createElement, Component } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, MapPin, Bed, Bath, Square, Check, Calendar, Phone, Download, Share2, Play, X, Sparkles, Home as HomeIcon, TreePine, ImageIcon, ShieldCheck, Wallet, Loader2, Timer } from 'lucide-react';
import { getPropertyBySlug, getRelated, Property } from '../data/properties';
const PHONE = '07061032706';
function StatusBadge({
  status
}: {
  status: Property['status'];
}) {
  const map = {
    Selling: 'bg-emerald-500 text-white',
    'Sold Out': 'bg-ink text-white',
    'Sale Closed': 'bg-ink/70 text-white'
  } as const;
  return <span className={`text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full ${map[status]}`} data-id="element-1245">
      {status === 'Selling' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-white mr-2 animate-pulse" data-id="element-1246" />}
      {status}
    </span>;
}
function CountdownTimer({
  endDate
}: {
  endDate: string;
}) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const target = new Date(endDate).getTime();
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const difference = target - now;
      if (difference <= 0) {
        clearInterval(interval);
        return;
      }
      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor(difference % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
        minutes: Math.floor(difference % (1000 * 60 * 60) / (1000 * 60)),
        seconds: Math.floor(difference % (1000 * 60) / 1000)
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [endDate]);
  return <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mt-4" data-id="element-1247">
      <div className="flex items-center gap-2 text-gold-dark text-[10px] font-bold uppercase tracking-widest mb-2" data-id="element-1248">
        <Timer className="w-3.5 h-3.5" data-id="element-1249" />
        Promo Price Ends In
      </div>
      <div className="grid grid-cols-4 gap-2 text-center" data-id="element-1250">
        <div className="bg-white rounded-lg py-2 shadow-sm" data-id="element-1251">
          <div className="font-serif text-lg text-ink leading-none" data-id="element-1252">
            {timeLeft.days}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-ink/50 mt-1" data-id="element-1253">
            Days
          </div>
        </div>
        <div className="bg-white rounded-lg py-2 shadow-sm" data-id="element-1254">
          <div className="font-serif text-lg text-ink leading-none" data-id="element-1255">
            {timeLeft.hours}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-ink/50 mt-1" data-id="element-1256">
            Hrs
          </div>
        </div>
        <div className="bg-white rounded-lg py-2 shadow-sm" data-id="element-1257">
          <div className="font-serif text-lg text-ink leading-none" data-id="element-1258">
            {timeLeft.minutes}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-ink/50 mt-1" data-id="element-1259">
            Min
          </div>
        </div>
        <div className="bg-white rounded-lg py-2 shadow-sm" data-id="element-1260">
          <div className="font-serif text-lg text-ink leading-none" data-id="element-1261">
            {timeLeft.seconds}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-ink/50 mt-1" data-id="element-1262">
            Sec
          </div>
        </div>
      </div>
    </div>;
}
export function PropertyDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const property = slug ? getPropertyBySlug(slug) : undefined;
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [priceLightbox, setPriceLightbox] = useState<number | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pdfModal, setPdfModal] = useState<{
    url: string;
    title: string;
  } | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  if (!property) {
    return <Navigate to="/houses" replace data-id="element-1263" />;
  }
  const related = getRelated(property);
  const isLand = property.category === 'land';
  const handleShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const data = {
      title: `${property.name} — Zylus Homes`,
      text: `${property.tagline}\n${property.location}`,
      url
    };
    if (navigator.share) {
      try {
        await navigator.share(data);
        return;
      } catch {
        /* fallback */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };
  const handleDownloadBrochure = () => {
    if (!property.brochureUrl || property.brochureUrl === '#') {
      // No real brochure yet — open WhatsApp request
      window.location.href = `https://wa.me/2347061032706?text=${encodeURIComponent(`Hello Zylus, please send me the brochure for ${property.name}.`)}`;
      return;
    }
    setIframeLoaded(false);
    setPdfModal({
      url: property.brochureUrl,
      title: `${property.name} Brochure`
    });
  };
  const handleDownloadFaq = () => {
    if (!property.faqUrl) return;
    setIframeLoaded(false);
    setPdfModal({
      url: property.faqUrl,
      title: `${property.name} Form & FAQ`
    });
  };
  const getEmbedUrl = (url: string) => {
    if (url.includes('drive.google.com') && url.includes('/view')) {
      return url.replace(/\/view.*/, '/preview');
    }
    return url;
  };
  return <div className="pt-24 pb-24 bg-bone" data-id="element-1264">
      {/* Back link */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mb-6" data-id="element-1265">
        <Link to={isLand ? '/land' : '/houses'} className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink transition-colors" data-id="element-1266">
          <ArrowLeft className="w-4 h-4" data-id="element-1267" />
          Back to {isLand ? 'Land' : 'Houses'}
        </Link>
      </div>

      {/* Hero */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12" data-id="element-1268">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start" data-id="element-1269">
          {/* Gallery */}
          <div className="lg:col-span-7" data-id="element-1270">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="relative rounded-3xl overflow-hidden bg-ink/5 aspect-[4/3] sm:aspect-[16/10]" data-id="element-1271">
              <AnimatePresence mode="wait" data-id="element-1272">
                <motion.img key={activeImg} src={property.gallery[activeImg] || property.hero} alt={property.name} initial={{
                opacity: 0,
                scale: 1.02
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.4
              }} className="absolute inset-0 w-full h-full object-cover cursor-zoom-in" onClick={() => setLightbox(activeImg)} data-id="element-1273" />
              </AnimatePresence>

              {/* Status + share overlays */}
              <div className="absolute top-4 left-4" data-id="element-1274">
                <StatusBadge status={property.status} data-id="element-1275" />
              </div>
              <button onClick={handleShare} aria-label="Share" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 hover:bg-gold text-ink flex items-center justify-center shadow-md transition-colors" data-id="element-1276">
                {copied ? <Check className="w-4 h-4" data-id="element-1277" /> : <Share2 className="w-4 h-4" data-id="element-1278" />}
              </button>

              {/* Video play overlay */}
              {property.videoId && <button onClick={() => setVideoOpen(true)} className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-ink/85 hover:bg-gold hover:text-ink text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded-full transition-colors backdrop-blur" data-id="element-1279">
                  <Play className="w-3.5 h-3.5 fill-current" data-id="element-1280" />
                  Watch Tour
                </button>}
            </motion.div>

            {/* Thumbnails */}
            {property.gallery.length > 1 && <div className="mt-3 grid grid-cols-4 gap-2" data-id="element-1281">
                {property.gallery.slice(0, 8).map((src, i) => <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${i === activeImg ? 'border-gold shadow-md' : 'border-transparent opacity-70 hover:opacity-100'}`} data-id="element-1282">
                    <img src={src} alt="" className="w-full h-full object-cover" data-id="element-1283" />
                  </button>)}
              </div>}
          </div>

          {/* Info card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="lg:col-span-5 lg:sticky lg:top-28 self-start" data-id="element-1284">
            <div className="bg-white rounded-3xl border border-ink/5 shadow-sm p-6 sm:p-8" data-id="element-1285">
              <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-3 inline-flex items-center gap-1.5" data-id="element-1286">
                {isLand ? <TreePine className="w-3.5 h-3.5" data-id="element-1287" /> : <HomeIcon className="w-3.5 h-3.5" data-id="element-1288" />}
                {property.type || (isLand ? 'Land' : 'House')}
              </div>
              <h1 className="text-3xl sm:text-4xl font-serif text-ink mb-3 leading-tight" data-id="element-1289">
                {property.name}
              </h1>
              <p className="text-ink/60 italic mb-4" data-id="element-1290">{property.tagline}</p>

              <div className="flex items-start gap-2 text-sm text-ink/70 mb-6" data-id="element-1291">
                <MapPin className="w-4 h-4 text-gold-dark mt-0.5 shrink-0" data-id="element-1292" />
                <span data-id="element-1293">{property.location}</span>
              </div>

              {/* Price */}
              <div className="bg-bone rounded-2xl p-5 mb-6" data-id="element-1294">
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-ink/50 mb-1" data-id="element-1295">
                  Pricing
                </div>
                <div className="font-serif text-2xl text-ink" data-id="element-1296">
                  {property.priceFrom || 'Enquire'}
                </div>
                {property.priceTiers && property.priceTiers.length > 0 && <ul className="mt-3 space-y-1.5 border-t border-ink/10 pt-3" data-id="element-1297">
                    {property.priceTiers.map(t => <li key={t.label} className="flex items-start justify-between gap-3 text-[13px]" data-id="element-1298">
                        <span className="text-ink/70 leading-snug" data-id="element-1299">
                          {t.label}
                        </span>
                        <span className="font-bold text-ink whitespace-nowrap" data-id="element-1300">
                          {t.price}
                        </span>
                      </li>)}
                  </ul>}
                {property.promoEndDate && <CountdownTimer endDate={property.promoEndDate} data-id="element-1301" />}
                {property.status === 'Selling' && !property.promoEndDate && <div className="text-[11px] text-ink/55 mt-3" data-id="element-1302">
                    Flexible payment plans · 6 – 24 months
                  </div>}
              </div>

              {/* Quick spec strip */}
              {!isLand && property.beds && <div className="grid grid-cols-3 gap-2 mb-6" data-id="element-1303">
                  <Spec icon={Bed} label="Beds" value={String(property.beds)} data-id="element-1304" />
                  <Spec icon={Bath} label="Baths" value={String(property.baths)} data-id="element-1305" />
                  <Spec icon={Square} label="Built-up" value={property.sqft || '—'} data-id="element-1306" />
                </div>}

              {isLand && property.plotSizes && <div className="grid grid-cols-3 gap-2 mb-6" data-id="element-1307">
                  {property.plotSizes.slice(0, 3).map((s, i) => <Spec key={i} icon={Square} label={`Plot ${i + 1}`} value={s} data-id="element-1308" />)}
                </div>}

              {/* CTAs */}
              <div className="space-y-2.5" data-id="element-1309">
                {property.status === 'Selling' ? <>
                    <Link to="/#book" className="flex items-center justify-center gap-2 w-full bg-gold hover:bg-gold-dark text-ink font-bold text-xs uppercase tracking-widest py-3.5 rounded-xl transition-colors shadow-lg shadow-gold/20" data-id="element-1310">
                      <Calendar className="w-4 h-4" data-id="element-1311" />
                      Book Inspection
                    </Link>
                    <div className="grid grid-cols-2 gap-2.5" data-id="element-1312">
                      <button onClick={handleDownloadBrochure} className="flex items-center justify-center gap-2 w-full bg-ink hover:bg-ink/90 text-white font-bold text-[10px] uppercase tracking-widest py-3.5 rounded-xl transition-colors" data-id="element-1313">
                        <Download className="w-3.5 h-3.5 text-gold" data-id="element-1314" />
                        Brochure
                      </button>
                      {property.faqUrl ? <button onClick={handleDownloadFaq} className="flex items-center justify-center gap-2 w-full bg-ink hover:bg-ink/90 text-white font-bold text-[10px] uppercase tracking-widest py-3.5 rounded-xl transition-colors" data-id="element-1315">
                          <Download className="w-3.5 h-3.5 text-gold" data-id="element-1316" />
                          Form & FAQ
                        </button> : <a href={`tel:${PHONE}`} className="flex items-center justify-center gap-2 w-full bg-bone hover:bg-ink hover:text-white text-ink font-bold text-[10px] uppercase tracking-widest py-3.5 rounded-xl transition-colors border border-ink/10" data-id="element-1317">
                          <Phone className="w-3.5 h-3.5" data-id="element-1318" />
                          Call {PHONE}
                        </a>}
                    </div>
                    {property.faqUrl && <a href={`tel:${PHONE}`} className="flex items-center justify-center gap-2 w-full bg-bone hover:bg-ink hover:text-white text-ink font-bold text-xs uppercase tracking-widest py-3.5 rounded-xl transition-colors border border-ink/10" data-id="element-1319">
                        <Phone className="w-4 h-4" data-id="element-1320" />
                        Call {PHONE}
                      </a>}
                  </> : <>
                    <div className="bg-ink/5 rounded-xl p-4 text-center" data-id="element-1321">
                      <div className="text-xs font-bold uppercase tracking-widest text-ink/60 mb-1" data-id="element-1322">
                        Estate Status
                      </div>
                      <div className="font-serif text-lg text-ink" data-id="element-1323">
                        {property.status}
                      </div>
                      <div className="text-[11px] text-ink/55 mt-1" data-id="element-1324">
                        Explore similar estates currently available below
                      </div>
                    </div>
                    <Link to={isLand ? '/land' : '/houses'} className="flex items-center justify-center gap-2 w-full bg-gold hover:bg-gold-dark text-ink font-bold text-xs uppercase tracking-widest py-3.5 rounded-xl transition-colors" data-id="element-1325">
                      Browse Available {isLand ? 'Land' : 'Houses'}
                      <ArrowRight className="w-4 h-4" data-id="element-1326" />
                    </Link>
                  </>}
              </div>
            </div>

            {/* Trust strip */}
            <div className="mt-4 bg-ink rounded-2xl p-5 text-white flex items-center gap-3" data-id="element-1327">
              <ShieldCheck className="w-5 h-5 text-gold shrink-0" data-id="element-1328" />
              <div className="text-xs leading-relaxed" data-id="element-1329">
                <span className="text-gold font-bold" data-id="element-1330">Verified by Zylus.</span>{' '}
                Backed by SCUML, LASRERA & CAC certifications.
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Intro */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mt-16 md:mt-24" data-id="element-1331">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-1332">
          <div className="lg:col-span-7" data-id="element-1333">
            <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-4" data-id="element-1334">
              Introduction
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-ink mb-6 leading-tight" data-id="element-1335">
              {property.tagline}
            </h2>
            <p className="text-ink/70 leading-relaxed text-[15px] sm:text-base" data-id="element-1336">
              {property.intro}
            </p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3" data-id="element-1337">
            {property.highlights.map(h => <motion.div key={h.label} whileHover={{
            y: -3
          }} className="bg-white rounded-2xl border border-ink/5 p-5" data-id="element-1338">
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-1.5" data-id="element-1339">
                  {h.label}
                </div>
                <div className="font-serif text-xl text-ink" data-id="element-1340">{h.value}</div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Estate features */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mt-16 md:mt-24" data-id="element-1341">
        <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-3" data-id="element-1342">
          Estate Features
        </div>
        <h2 className="text-3xl md:text-4xl font-serif text-ink mb-8" data-id="element-1343">
          What life looks like here.
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-id="element-1344">
          {property.features.map((f, i) => <motion.div key={f} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.04
        }} className="bg-white rounded-2xl border border-ink/5 p-4 sm:p-5 flex items-center gap-3 hover:border-gold/40 hover:shadow-md transition-all" data-id="element-1345">
              <div className="w-9 h-9 rounded-xl bg-gold/15 flex items-center justify-center shrink-0" data-id="element-1346">
                <Check className="w-4 h-4 text-gold-dark" strokeWidth={3} data-id="element-1347" />
              </div>
              <span className="text-sm font-medium text-ink leading-tight" data-id="element-1348">
                {f}
              </span>
            </motion.div>)}
        </div>
      </section>

      {/* Gallery */}
      {property.gallery.length > 0 && <section className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mt-16 md:mt-24" data-id="element-1349">
          <div className="flex items-end justify-between mb-6" data-id="element-1350">
            <div data-id="element-1351">
              <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-3" data-id="element-1352">
                Gallery
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-ink" data-id="element-1353">
                Take a closer look.
              </h2>
            </div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-ink/40 hidden sm:flex items-center gap-1.5" data-id="element-1354">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-1355" />
              Tap to expand
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-id="element-1356">
            {property.gallery.map((src, i) => <motion.button key={i} onClick={() => setLightbox(i)} whileHover={{
          scale: 1.02
        }} className={`relative aspect-[4/3] overflow-hidden rounded-2xl bg-ink/5 ${i === 0 ? 'md:col-span-2 md:row-span-2 md:aspect-square' : ''}`} data-id="element-1357">
                <img src={src} alt={`${property.name} ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-id="element-1358" />
              </motion.button>)}
          </div>
        </section>}

      {/* Price Breakdown Graphics */}
      {property.priceGraphics && property.priceGraphics.length > 0 && <section className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mt-16 md:mt-24" data-id="element-1359">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8" data-id="element-1360">
            <div data-id="element-1361">
              <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-3 inline-flex items-center gap-1.5" data-id="element-1362">
                <Wallet className="w-3.5 h-3.5" data-id="element-1363" />
                Price Breakdown
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-ink leading-tight" data-id="element-1364">
                Payment plans, in detail.
              </h2>
              <p className="text-ink/60 mt-2 text-sm max-w-xl" data-id="element-1365">
                Flexible options designed to fit your timeline — from a straight
                purchase to a 12-month spread.
              </p>
            </div>
            {property.priceTiers && property.priceTiers.length > 0 && <div className="bg-white rounded-2xl border border-ink/5 p-4 min-w-[260px]" data-id="element-1366">
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-ink/50 mb-2" data-id="element-1367">
                  Starting Prices
                </div>
                <ul className="space-y-1.5" data-id="element-1368">
                  {property.priceTiers.map(t => <li key={t.label} className="flex items-start justify-between gap-3 text-[13px]" data-id="element-1369">
                      <span className="text-ink/70 leading-snug" data-id="element-1370">
                        {t.label}
                      </span>
                      <span className="font-bold text-ink whitespace-nowrap" data-id="element-1371">
                        {t.price}
                      </span>
                    </li>)}
                </ul>
              </div>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-id="element-1372">
            {property.priceGraphics.map((src, i) => <motion.button key={i} onClick={() => setPriceLightbox(i)} whileHover={{
          y: -4
        }} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.05
        }} className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-ink/5 border border-ink/5 shadow-sm hover:shadow-xl hover:border-gold/40 transition-all" data-id="element-1373">
                <img src={src} alt={`${property.name} payment plan ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" data-id="element-1374" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1375" />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white text-[10px] font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1376">
                  <span data-id="element-1377">Plan {i + 1}</span>
                  <span className="inline-flex items-center gap-1" data-id="element-1378">
                    <ImageIcon className="w-3 h-3" data-id="element-1379" />
                    Expand
                  </span>
                </div>
              </motion.button>)}
          </div>
        </section>}

      {/* Flyer + Video CTA strip */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mt-16 md:mt-24" data-id="element-1380">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1381">
          {/* Brochure */}
          <motion.div whileHover={{
          y: -4
        }} className="bg-ink rounded-3xl p-8 text-white relative overflow-hidden" data-id="element-1382">
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gold/20 blur-3xl" data-id="element-1383" />
            <Sparkles className="w-8 h-8 text-gold mb-4 relative" data-id="element-1384" />
            <h3 className="font-serif text-2xl mb-2 relative" data-id="element-1385">
              Get the full brochure
            </h3>
            <p className="text-white/60 text-sm mb-5 leading-relaxed relative" data-id="element-1386">
              Floorplans, pricing, payment terms, and the complete estate
              feature list — delivered straight to you.
            </p>
            <button onClick={handleDownloadBrochure} className="relative bg-gold hover:bg-gold-dark text-ink font-bold text-xs uppercase tracking-widest px-5 py-3 rounded-xl transition-colors inline-flex items-center gap-2" data-id="element-1387">
              <Download className="w-3.5 h-3.5" data-id="element-1388" />
              Download / Request
            </button>
          </motion.div>

          {/* Video / Flyer */}
          {property.videoId ? <motion.button whileHover={{
          y: -4
        }} onClick={() => setVideoOpen(true)} className="group bg-bone rounded-3xl p-0 relative overflow-hidden text-left aspect-video md:aspect-auto" data-id="element-1389">
              <img src={`https://img.youtube.com/vi/${property.videoId}/maxresdefault.jpg`} alt={property.name} className="absolute inset-0 w-full h-full object-cover" onError={e => {
            ;
            (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${property.videoId}/hqdefault.jpg`;
          }} data-id="element-1390" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" data-id="element-1391" />
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-1392">
                <div className="w-16 h-16 rounded-full bg-gold flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform" data-id="element-1393">
                  <Play className="w-6 h-6 text-ink fill-ink ml-1" data-id="element-1394" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white" data-id="element-1395">
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-gold mb-1" data-id="element-1396">
                  Video Tour
                </div>
                <div className="font-serif text-xl" data-id="element-1397">
                  Walk through the estate
                </div>
              </div>
            </motion.button> : property.flyerImage ? <motion.div whileHover={{
          y: -4
        }} className="bg-bone rounded-3xl overflow-hidden" data-id="element-1398">
              <img src={property.flyerImage} alt={`${property.name} flyer`} className="w-full h-full object-cover aspect-video" data-id="element-1399" />
            </motion.div> : null}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && <section className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mt-16 md:mt-24" data-id="element-1400">
          <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-3" data-id="element-1401">
            Similar {isLand ? 'Land' : 'Estates'}
          </div>
          <h2 className="text-3xl md:text-4xl font-serif text-ink mb-8" data-id="element-1402">
            You may also like
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1403">
            {related.map(r => <Link key={r.id} to={`/${isLand ? 'land' : 'houses'}/${r.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-ink/5 shadow-sm hover:shadow-xl hover:border-gold/30 transition-all duration-500" data-id="element-1404">
                <div className="relative aspect-[4/3] overflow-hidden bg-ink/5" data-id="element-1405">
                  <img src={r.hero} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-1406" />
                  <div className="absolute top-3 left-3" data-id="element-1407">
                    <StatusBadge status={r.status} data-id="element-1408" />
                  </div>
                </div>
                <div className="p-5" data-id="element-1409">
                  <div className="font-serif text-lg text-ink mb-1 group-hover:text-gold-dark transition-colors" data-id="element-1410">
                    {r.name}
                  </div>
                  <div className="text-xs text-ink/50 mb-2" data-id="element-1411">{r.location}</div>
                  <div className="text-sm font-bold text-ink" data-id="element-1412">
                    {r.priceFrom}
                  </div>
                </div>
              </Link>)}
          </div>
        </section>}

      {/* Lightbox */}
      <AnimatePresence data-id="element-1413">
        {lightbox !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setLightbox(null)} className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4" data-id="element-1414">
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-gold hover:text-ink text-white flex items-center justify-center" data-id="element-1415">
              <X className="w-5 h-5" data-id="element-1416" />
            </button>
            <motion.img key={lightbox} src={property.gallery[lightbox]} alt="" initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="max-w-[95vw] max-h-[90vh] object-contain rounded-xl" data-id="element-1417" />
          </motion.div>}
      </AnimatePresence>

      {/* Price graphics lightbox */}
      <AnimatePresence data-id="element-1418">
        {priceLightbox !== null && property.priceGraphics && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setPriceLightbox(null)} className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4" data-id="element-1419">
            <button onClick={() => setPriceLightbox(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-gold hover:text-ink text-white flex items-center justify-center" data-id="element-1420">
              <X className="w-5 h-5" data-id="element-1421" />
            </button>
            <motion.img key={priceLightbox} src={property.priceGraphics[priceLightbox]} alt="" initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="max-w-[95vw] max-h-[90vh] object-contain rounded-xl" data-id="element-1422" />
          </motion.div>}
      </AnimatePresence>

      {/* Video modal */}
      <AnimatePresence data-id="element-1423">
        {videoOpen && property.videoId && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setVideoOpen(false)} className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4" data-id="element-1424">
            <motion.div initial={{
          scale: 0.92,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.92,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-4xl bg-ink rounded-2xl overflow-hidden" data-id="element-1425">
              <button onClick={() => setVideoOpen(false)} className="absolute -top-12 right-0 md:top-3 md:right-3 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-gold hover:text-ink text-white flex items-center justify-center" data-id="element-1426">
                <X className="w-5 h-5" data-id="element-1427" />
              </button>
              <div className="aspect-video" data-id="element-1428">
                {property.videoId?.includes('.mp4') ? <video src={property.videoId} controls autoPlay className="w-full h-full object-contain bg-black" data-id="element-1429" /> : <iframe src={`https://www.youtube.com/embed/${property.videoId}?autoplay=1&rel=0`} title={property.name} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-1430" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* PDF Modal */}
      <AnimatePresence data-id="element-1431">
        {pdfModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setPdfModal(null)} className="fixed inset-0 z-[80] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-6" data-id="element-1432">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-5xl h-[85vh] bg-bone rounded-2xl overflow-hidden flex flex-col shadow-2xl" data-id="element-1433">
              {/* Header */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 bg-white border-b border-ink/10" data-id="element-1434">
                <h3 className="font-serif text-lg sm:text-xl text-ink truncate pr-4" data-id="element-1435">
                  {pdfModal.title}
                </h3>
                <div className="flex items-center gap-2 sm:gap-4 shrink-0" data-id="element-1436">
                  <a href={pdfModal.url} target="_blank" rel="noopener noreferrer" className="hidden sm:flex text-[10px] font-bold uppercase tracking-widest text-ink/60 hover:text-gold-dark items-center gap-1.5 transition-colors" data-id="element-1437">
                    <Download className="w-3.5 h-3.5" data-id="element-1438" />
                    Open Original
                  </a>
                  <button onClick={() => setPdfModal(null)} className="w-8 h-8 rounded-full bg-ink/5 hover:bg-ink hover:text-white text-ink flex items-center justify-center transition-colors" data-id="element-1439">
                    <X className="w-4 h-4" data-id="element-1440" />
                  </button>
                </div>
              </div>
              {/* Iframe */}
              <div className="flex-1 w-full bg-ink/5 relative flex items-center justify-center" data-id="element-1441">
                {!iframeLoaded && <div className="absolute inset-0 flex flex-col items-center justify-center text-ink/50 gap-3" data-id="element-1442">
                    <Loader2 className="w-8 h-8 animate-spin text-gold" data-id="element-1443" />
                    <div className="text-xs font-bold uppercase tracking-widest" data-id="element-1444">
                      Loading Document...
                    </div>
                  </div>}
                <iframe src={getEmbedUrl(pdfModal.url)} className={`absolute inset-0 w-full h-full border-0 transition-opacity duration-500 ${iframeLoaded ? 'opacity-100' : 'opacity-0'}`} title={pdfModal.title} onLoad={() => setIframeLoaded(true)} data-id="element-1445" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function Spec({
  icon: Icon,
  label,
  value
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  value: string;
}) {
  return <div className="bg-bone rounded-xl p-3 text-center" data-id="element-1446">
      <Icon className="w-4 h-4 text-gold-dark mx-auto mb-1" data-id="element-1447" />
      <div className="text-[9px] font-bold tracking-widest uppercase text-ink/50" data-id="element-1448">
        {label}
      </div>
      <div className="font-serif text-sm text-ink mt-0.5" data-id="element-1449">{value}</div>
    </div>;
}